import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { ApiResponse } from "@shared/types/api";

export type ReportStatus = "pending" | "reviewed" | "dismissed";

export interface AdminEventReport {
  id: string;
  reason: string;
  description: string | null;
  status: ReportStatus;
  createdAt: string;
  eventId: string;
  eventName: string | null;
  eventSlug: string | null;
  eventDate: string | null;
  eventReviewStatus: string | null;
  reporterFirstName: string | null;
  reporterLastName: string | null;
  reporterEmail: string | null;
}

type ReportsApiResponse = ApiResponse<{
  reports: AdminEventReport[];
  counts: { pending: number; reviewed: number; dismissed: number };
}>;

export function useAdminEventReports(status: "all" | ReportStatus = "all") {
  return useQuery<ReportsApiResponse>({
    queryKey: ["admin-event-reports", status],
    queryFn: (): Promise<ReportsApiResponse> =>
      api.get(`/admin/event-reports${status !== "all" ? `?status=${status}` : ""}`),
  });
}

export function useUpdateReportStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ReportStatus }) =>
      api.patch(`/admin/event-reports/${id}/status`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-event-reports"] }),
  });
}
